/** Billing and Dues copy. English only. */
export const billing = {
  title: "Billing",
  subtitle: "Bills, payments, GST invoices, credit notes and refunds.",
  newBill: "New bill",
  tabs: { bills: "Bills", payments: "Payments", invoices: "Invoices", creditNotes: "Credit notes", refunds: "Refunds" },
  notConnected: "Not connected yet",
  loadMore: "Load more",
  retry: "Retry",
  empty: {
    bills: ["No bills today", "Bills raised at the counter show up here."] as const,
    payments: ["No payments yet", "Cash, UPI and member credit payments show up here."] as const,
    invoices: ["No invoices yet", "An invoice is issued with every bill."] as const,
    creditNotes: ["No credit notes", "Credit a bill from its page when money has to go back."] as const,
    refunds: ["No refunds due", "Refunds raised by credit notes or cancelled classes wait here."] as const,
  },
  filters: { all: "All", unpaid: "Unpaid", partial: "Part paid", paid: "Paid", void: "Void", method: "Method", allMethods: "All methods", status: "Status" },
  cols: { bill: "Bill", member: "Member", date: "Date", total: "Total", balance: "Balance", status: "Status", method: "Method", amount: "Amount", invoice: "Invoice", reason: "Credit note", settlement: "Settlement", refund: "Refund" },
  methods: { cash: "Cash", member_credit: "Member credit", upi_qr: "UPI QR", payment_link: "Payment link" } as Record<string, string>,
  payStatus: { unpaid: "Unpaid", partial: "Part paid", paid: "Paid", overpaid: "Overpaid", void: "Void" } as Record<string, string>,
  settlements: { reduce_balance: "Reduce balance", member_credit: "To member credit", refund: "Refund" } as Record<string, string>,
  export: "Export",
  exportDone: (n: number) => `Exported ${n} rows`,
  hidden: "Hidden",
  refundStatus: { pending: "Pending", processing: "Processing", processed: "Processed", failed: "Failed" } as Record<string, string>,
  execute: "Execute refund",
  executed: "Refund sent",
  classRefunds: "Class refunds due",
  classRefundRow: (cls: string, date: string) => `${cls}, ${date}`,
  counter: {
    title: "New bill",
    renewTitle: "Renew plan",
    upgradeTitle: "Upgrade plan",
    member: "Member",
    memberSearch: "Search member by name or phone",
    memberNone: "No member matches",
    change: "Change",
    items: "Plans and products",
    itemSearch: "Filter items",
    itemsEmpty: "Nothing is sellable at this branch.",
    cart: "Cart",
    cartEmpty: "Add a plan or product to start the bill.",
    qty: (n: string) => `Quantity of ${n}`,
    remove: (n: string) => `Remove ${n}`,
    discount: "Discount %",
    discountCap: (pct: string) => `Your limit is ${pct}%`,
    discountOver: (pct: string) => `Above your limit of ${pct}%`,
    discountBad: "Enter a percentage like 10 or 12.5",
    coupon: "Coupon code",
    notes: "Notes",
    payNow: "Take payment now",
    payFull: "Pay the full total",
    payAmount: "Amount",
    payBad: "Enter an amount like 1500 or 1500.50",
    addPayment: "Add tender",
    tender: "Tender",
    summary: { subtotal: "Subtotal", discount: "Discount", tax: "GST", round: "Rounding", total: "Total", due: "Balance after payments" },
    updating: "Updating the total…",
    stale: "The cart changed. Waiting for the new total.",
    previewError: "Could not price this cart",
    issue: "Issue bill",
    issuing: "Issuing…",
    checking: "Checking…",
    checkingBody: "The connection dropped. Retrying with the same request so no second bill is created.",
    issued: "Bill issued",
    renewPrefill: "Renewing",
    upgradePick: "Pick the plan to upgrade to. The server prices the change when you confirm.",
    upgradeNoPreview: "The upgrade price is worked out by the server when you confirm.",
    upgradeConfirm: "Upgrade plan",
    upgradeLever: "Upgrading plans is not switched on for your role.",
    renewLever: "Renewing plans is not switched on for your role.",
    planLoadError: "Could not load that plan",
  },
  bill: {
    back: "Back to billing",
    status: "Status",
    lines: "Lines",
    payments: "Payments",
    dues: "Instalments",
    noPayments: "No payments yet",
    collect: "Collect payment",
    collectTitle: "Collect payment",
    upi: "UPI QR",
    link: "Payment link",
    print: "Print invoice",
    noInvoice: "No invoice for this bill",
    void: "Void bill",
    voidTitle: "Void this bill?",
    voidReason: "Reason",
    voidConfirm: "Void bill",
    voidBody: "A bill with payments or an invoice cannot be voided; credit it instead.",
    voided: "Bill voided",
    credit: "Credit note",
    creditTitle: "Credit this bill",
    creditSettle: "Settle by",
    creditConfirm: "Issue credit note",
    creditDone: "Credit note issued",
    creditLines: "Lines to credit in full",
    paid: "Payment recorded",
    review: "Needs review",
    reviewBody: "Money arrived on this payment request and needs a person to decide.",
    apply: "Apply payment",
    reject: "Reject",
    resolved: "Resolved",
    voidedOn: (d: string) => `Voided ${d}`,
  },
  upi: {
    title: "Collect by UPI",
    linkTitle: "Collect by payment link",
    qrLabel: (amount: string) => `QR code to pay ${amount} by UPI`,
    payee: (p: string) => `Paying ${p}`,
    scan: "Ask the member to scan or open this UPI request.",
    open: "Open in a UPI app",
    copy: "Copy",
    copied: "Copied",
    waiting: "Waiting for payment…",
    paid: "Payment received",
    expired: "This request has expired",
    cancelled: "Cancelled",
    failed: "The payment failed",
    review: "Needs review: money arrived that does not match",
    recheck: "Recheck",
    cancel: "Cancel request",
    close: "Close",
    printInvoice: "Print invoice",
    stub: "Test stub: no real payment is taken.",
  },
  invoice: {
    print: "Print",
    a4: "A4",
    receipt: "80 mm receipt",
    taxInvoice: "Tax invoice",
    billOfSupply: "Bill of supply",
    creditNote: "Credit note",
    seller: "Seller",
    buyer: "Billed to",
    gstin: "GSTIN",
    place: "Place of supply",
    hsn: "HSN/SAC",
    qty: "Qty",
    rate: "Rate",
    taxable: "Taxable",
    cgst: "CGST",
    sgst: "SGST",
    igst: "IGST",
    total: "Total",
    round: "Round off",
    grand: "Grand total",
    summary: "Tax summary",
    against: (n: string, d: string) => `Against invoice ${n} dated ${d}`,
  },
  dues: {
    title: "Dues",
    subtitle: "Unpaid and part-paid bills, oldest first.",
    empty: "No dues: everyone is paid up",
    emptyBody: "Nothing is waiting to be collected.",
    collect: "Collect",
    whatsapp: "WhatsApp",
    buckets: { b0: "0–7 days", b1: "8–30 days", b2: "30+ days", notDue: "Not due yet", noDate: "No due date" },
    capped: "200+ bills. Showing the first pages only.",
    waLabel: (n: string) => `WhatsApp ${n}`,
    collectLabel: (n: string) => `Collect from ${n}`,
    overdueBy: (n: number) => `${n} day${n === 1 ? "" : "s"} overdue`,
    owed: "Owed",
  },
  errors: { generic: "Something went wrong" },
};
